/* eslint-disable no-plusplus,no-underscore-dangle */
import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';
import { naturalSorting } from '@/utils/experience-utils';
import { VSpace } from '@/components/spacing';
import { Tooltip } from '@/components/tooltip';
import { getChanceToWin } from '@/features/details/summary/util';
import {
  Header,
  SummaryHeader,
} from '@/features/details/summary/summary-header';
import {
  SummaryBody,
  SummaryValue,
  VersionName,
} from '@/features/details/summary/summary-value';
import { Card } from '@/components/card';
import { SummaryContext } from '@/features/details/summary/context';
import { SummaryResponse } from '@/webapi/use-analytics-api';
import {
  Experience,
  ExperienceStatus,
  ExperienceVariant,
  useExperienceApi,
} from '@/webapi/use-experience-api';
import { allMetricKinds, MetricKind } from '@/webapi/models';
import { maybe } from '@/features/details/utils';
import { CONTROL } from '@/features/details/shared';
import { clone } from '@/features/editor/widgets/shared/modals/audience/facebook-audience/ad-search/shared';
import { DatePickerContext } from '@/features/details/date-pick/context';
import { formatEnum } from '@/utils/types';
import {
  allowProfit,
  allowReturns,
  selectedGoal,
} from '@/features/details/charts/more-metrics';
import {
  GoalChip,
  GoalsWrapper,
} from '@/features/editor/widgets/targeting/allocation-card';

export function SummaryTable({
  status,
  experience,
  setExperience,
}: {
  status: ExperienceStatus;
  experience: Experience;
  setExperience: (e: Experience) => void;
}) {
  const {
    moreMetrics,
    metricsPerRow,
    showSummary,
    variants,
    hasOneVariant,
    data,
    primaryMetrics,
    largeHeaders,
    hasMoreMetrics,
    primaryGoal,
  } = useContext(SummaryContext);
  const { upsertExperience, loading } = useExperienceApi(
    experience.lastDevicePreview,
    experience,
  );
  const allMetrics = allMetricKinds(experience)
    .filter(allowProfit(experience))
    .filter(allowReturns(experience));
  const addMoreMetrics = useMemo(
    () =>
      allMetrics?.filter(
        (value) =>
          !primaryMetrics?.includes(value) && !moreMetrics?.includes(value),
      ),
    [experience, primaryMetrics, moreMetrics],
  );

  const { version } = useContext(DatePickerContext);
  let _variants = variants;
  if (version?.isCurrent && status === ExperienceStatus.PUBLISHED) {
    _variants = variants.filter((v) => v.publishedChance > 0);
  }
  if (maybe(() => _variants.length, 0) === 0 || !showSummary) {
    return null;
  }

  const variant100 = _variants.find((v) => v.publishedChance === 100);
  if (variant100 && version?.isCurrent) {
    _variants = [variant100];
  }

  return (
    <>
      <DetailsCard hasOneVariant={hasOneVariant}>
        <SummaryRow key="iddqd" columns={metricsPerRow}>
          {!hasOneVariant && (
            <SummaryHeader isLarge={largeHeaders}>Variation</SummaryHeader>
          )}
          {primaryMetrics.map((h) => (
            <Header value={h} key={h} isLarge={largeHeaders} />
          ))}
          {!hasOneVariant && (
            <SummaryHeader>
              Odds to Win
              <Tooltip
                zIndex={0}
                title="Odds to Win"
                text="The probability Version A is Better than Version B. Based on the primary metric."
              />
            </SummaryHeader>
          )}
        </SummaryRow>
        {_variants?.sort(naturalSorting).map((v) => (
          <SummaryRow key={v.id} columns={metricsPerRow}>
            {!hasOneVariant && <VersionName v={v} />}
            {primaryMetrics.map((metric) => (
              <SummaryValue key={metric} metric={metric} variant={v} />
            ))}
            {!hasOneVariant && (
              <SummaryBody>
                <span>
                  {getFormattedChanceToWin({
                    data,
                    v,
                    primaryGoal,
                    variants: _variants,
                  })}
                </span>
              </SummaryBody>
            )}
          </SummaryRow>
        ))}
      </DetailsCard>
      {hasMoreMetrics && (
        <>
          <VSpace value={2} />
          <DetailsCard>
            <SummaryRow key="iddqd" columns={metricsPerRow}>
              {!hasOneVariant && <SummaryHeader>Variation</SummaryHeader>}
              {moreMetrics
                .filter((_, idx) => idx < 7)
                .map((metric) => (
                  <Header key={metric} value={metric} isLarge={largeHeaders} />
                ))}
            </SummaryRow>
            {_variants?.sort(naturalSorting).map((v) => (
              <SummaryRow key={v.id} columns={metricsPerRow}>
                {!hasOneVariant && <VersionName v={v} />}
                {moreMetrics
                  .filter((_, idx) => idx < 7)
                  .map((metric) => (
                    <SummaryValue key={metric} metric={metric} variant={v} />
                  ))}
              </SummaryRow>
            ))}
            {moreMetrics?.length <= (hasOneVariant ? 6 : 5) &&
              addMoreMetrics?.length > 0 && (
                <AddMoreWrapper
                  style={{
                    width: `calc((100% - 70px + 2rem)*(${
                      (hasOneVariant ? 8 : 7) - moreMetrics.length
                    }/8) - 2rem)`,
                  }}
                >
                  <Title>Would like to see more reports?</Title>
                  <MetricsList style={{ opacity: loading ? 0.15 : 1 }}>
                    {addMoreMetrics.map((metric) => {
                      const onClick = async () => {
                        if (loading || metric === experience.primaryGoal) {
                          return;
                        }
                        const cloned = JSON.parse(
                          JSON.stringify(experience),
                        ) as Experience;
                        cloned.goals.push(metric);
                        setExperience(cloned);
                        await upsertExperience(cloned);
                      };
                      return !selectedGoal(experience, metric) ? (
                        <Chip
                          focused={selectedGoal(experience, metric)}
                          key={metric}
                          onClick={onClick}
                        >
                          {formatEnum(metric, true)}
                        </Chip>
                      ) : null;
                    })}
                  </MetricsList>
                </AddMoreWrapper>
              )}
          </DetailsCard>
        </>
      )}
    </>
  );
}

const SummaryRow = styled.div`
  display: grid;
  grid-template-columns: repeat(${(p: any) => p.columns}, 1fr);
  grid-column-gap: 2rem;
  text-align: start;
`;

const DetailsCard = styled(Card)`
  && {
    cursor: default;
    display: grid;
    position: relative;
    grid-row-gap: 2.5rem;
    padding: ${(p: { hasOneVariant: boolean }) =>
      p?.hasOneVariant ? `3.5rem 5rem 4rem 5rem` : `3.5rem`};
    border-radius: 2rem;
  }
`;

function isMultiVariantNoControl(
  hasControl: boolean,
  variants: Array<ExperienceVariant>,
) {
  return !hasControl && variants?.length === 2;
}

export function getFormattedChanceToWin({
  data,
  v,
  primaryGoal,
  variants,
}: {
  data: SummaryResponse;
  v: ExperienceVariant;
  primaryGoal: MetricKind;
  variants: Array<ExperienceVariant>;
}) {
  let hasControl = dataHasControl(variants, data);
  if (isMultiVariantNoControl(hasControl, variants)) {
    data = clone(data);
    if (maybe(() => v.id === data.controlVariationId)) {
      v = clone(v);
      v.name = CONTROL;
    }
    hasControl = true;
  }
  let chanceToWin: number | string = getChanceToWin(data, v, primaryGoal);
  const isMultiVariantControl = variants?.length > 2 && v.name === CONTROL;

  if (!hasControl) {
    return `---`;
  }

  if (chanceToWin <= 1) {
    chanceToWin = `<1`;
  }
  return isMultiVariantControl ? `---` : `${chanceToWin}%`;
}
export function dataHasControl(
  variants: Array<ExperienceVariant>,
  data: SummaryResponse,
) {
  const dataVariantIds = data?.variants?.map((v) => v.variantId);
  return !!variants
    ?.filter((v) => dataVariantIds.includes(v.id))
    ?.map((v) => v.name)
    ?.find((n) => n === CONTROL);
}

const AddMoreWrapper = styled.div`
  position: absolute;
  height: 100%;
  right: 35px;
  padding-top: 35px;
`;

const Title = styled.p`
  color: #8fa3ad;
  font-size: 1.2rem;
  font-weight: 500;
  font-family: 'Inter', serif;
  margin: 0 0 20px;
`;

const Chip = styled(GoalChip)`
  && {
    background-color: #eff0f3;
    color: #0c0c0c;
    margin: 0 !important;
    font-weight: 600;
    font-size: 1rem;
    border: none;
    opacity: 1;
    text-transform: uppercase;
  }

  &&:hover {
    background-color: #e0e0e5;
  }
`;

const MetricsList = styled(GoalsWrapper)`
  && {
    overflow: scroll;
    max-height: calc(100% - 35px);
    margin: 0 auto;
    gap: 1.2rem 1rem;
    padding: 0 0 2rem 0;
    max-width: 100%;
    flex-wrap: wrap;
    justify-content: start;
    align-items: center;
  }
`;
